import { SettingsAdjust } from '@carbon/react/icons';
import {
  Button,
  InlineNotification,
  OverflowMenu,
  RadioButton,
  RadioButtonGroup,
} from '@carbon/react';
import { useAtom } from 'jotai';
import { useState } from 'react';
import { parseLocalDateTime } from 'utils/dates';
import { formatMutationResource } from 'utils/misc';
import { wellPerforationFactorMutationAtom } from './atoms';
import { convertValueToFactor } from './utils';
import type { PerforationAdjustmentValue } from './types';
import styles from './factor-popup.module.scss';
import perforationStyles from './perforation-adjustment-popup.module.scss';

interface PerforationAdjustmentPopupProps {
  uwi: string;
  value: PerforationAdjustmentValue;
  onChange: (value: PerforationAdjustmentValue) => void;
  onApplySuccess?: (timestamp: string) => void;
  onApplyError?: (message: string) => void;
}

export const PerforationAdjustmentPopup = ({
  uwi,
  value,
  onChange,
  onApplySuccess,
  onApplyError,
}: PerforationAdjustmentPopupProps) => {
  const [perforationFactorData, updatePerforationFactor] = useAtom(
    wellPerforationFactorMutationAtom
  );
  const { loading } = formatMutationResource(perforationFactorData);
  const [selectedValue, setSelectedValue] =
    useState<PerforationAdjustmentValue>(value);

  const isSelectionModified = selectedValue !== value;

  const handleApply = () => {
    const factor = convertValueToFactor(selectedValue);
    updatePerforationFactor({
      url: `/wells/${uwi}`,
      type: 'PATCH',
      data: { estimatedPerforatedLateralLengthFactor: factor },
      onSuccess: (data: { updatedAt?: string }) => {
        onChange(selectedValue);
        const timestamp = data?.updatedAt
          ? parseLocalDateTime(data.updatedAt, true)
          : parseLocalDateTime(new Date().toISOString(), true);
        onApplySuccess?.(timestamp);
      },
      onError: (error?: unknown) => {
        const errorData = error as {
          errors?: Array<{
            message: string;
            updatedAt?: string;
            code?: string;
          }>;
        };

        let errorMsg =
          'The Estimated Perforated Lateral Length Adjustment Factor failed updating.';

        if (errorData?.errors && errorData.errors.length > 0) {
          const firstError = errorData.errors[0];
          const code = errorData.errors[0].code;
          if (code === 'ESTIMATED_PERFORATED_LATERAL_CALCULATION_FAILED') {
            onChange(selectedValue);
          }
          const timestamp = firstError.updatedAt
            ? parseLocalDateTime(firstError.updatedAt, true)
            : '';
          errorMsg = timestamp
            ? `${firstError.message} ${timestamp}`
            : firstError.message;
        }

        onApplyError?.(errorMsg);
      },
    });
  };

  const handleReset = () => {
    setSelectedValue(value);
  };

  const getButtonLabel = () => {
    if (value === 'none' || value === null) {
      return 'No Adjustment';
    }
    return `${value}% Adjustment`;
  };

  return (
    <div>
      <div className={styles.menuWrapper}>
        <div className={styles.triggerContainer}>
          <span className={styles.triggerLabel}>{getButtonLabel()}</span>
          <OverflowMenu
            renderIcon={SettingsAdjust}
            size="lg"
            flipped
            className={styles.overflowMenu}
            menuOptionsClass={styles.overflowMenuOptions}
            iconDescription="Adjustment settings"
            ariaLabel={getButtonLabel()}
          >
            <div className={styles.menu} data-adjustment-menu="true">
              <h4 className={styles.menuTitle}>
                Estimated Perforated Lateral Length Adjustment
              </h4>

              <p className={styles.menuDescription}>
                This setting applies a percentage to the measured lateral length
                to estimate the likely perforated interval when actual
                perforation data is not available. Some wells include
                unperforated footage near the heel or toe, so using the full
                drilled length can overstate the true perf interval.
              </p>

              <p className={styles.menuDescription}>
                Lower percentages assume more unperforated footage. Higher
                percentages assume most of the lateral is perforated.
              </p>

              <RadioButtonGroup
                name="perforation-adjustment"
                valueSelected={selectedValue ?? value ?? 'none'}
                onChange={(val) =>
                  setSelectedValue(val as PerforationAdjustmentValue)
                }
                orientation="horizontal"
                className={perforationStyles.radioGroup}
              >
                <RadioButton id="adj-92" value="92" labelText="92%" />
                <RadioButton id="adj-95" value="95" labelText="95%" />
                <RadioButton id="adj-98" value="98" labelText="98%" />
                <RadioButton id="adj-99" value="99" labelText="99%" />
                <RadioButton id="adj-none" value="none" labelText="100%" />
              </RadioButtonGroup>

              <InlineNotification
                kind="info"
                lowContrast
                hideCloseButton
                className={styles.infoNotification}
                title="Estimated perforated length"
                subtitle="= measured lateral length × selected factor. Values close to 100% assume nearly the entire lateral is perforated; lower values account for un-treated segments."
              />

              <div className={styles.menuFooter}>
                <Button
                  kind="secondary"
                  onClick={handleReset}
                  disabled={loading || !isSelectionModified}
                >
                  Reset
                </Button>
                <Button
                  kind="primary"
                  onClick={handleApply}
                  disabled={loading || !isSelectionModified}
                >
                  {loading ? 'Applying...' : 'Apply'}
                </Button>
              </div>
            </div>
          </OverflowMenu>
        </div>
      </div>
    </div>
  );
};
